import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { LogIn, LogOut, Clock, Loader2, MapPin } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { formatDateBR, formatTimeBR, durationBetween } from "../lib/format";
import { PageHeader } from "../components/Layout";
import { Filters } from "../components/Filters";
import { useAuth } from "../context/AuthContext";

export default function Timecards() {
    const { user } = useAuth();
    const qc = useQueryClient();
    const [filters, setFilters] = useState({ q: "", start: "", end: "" });
    const [busy, setBusy] = useState(false);

    const params = new URLSearchParams();
    if (filters.q) params.set("q", filters.q);
    if (filters.start) params.set("start", filters.start);
    if (filters.end) params.set("end", filters.end);

    const { data: cards = [] } = useQuery({ queryKey: ["timecards", filters], queryFn: async () => (await api.get(`/timecards?${params}`)).data });
    const { data: status } = useQuery({ queryKey: ["timecard-status"], queryFn: async () => (await api.get("/timecards/status")).data, enabled: user?.role === "colaborador" });

    const isColaborador = user?.role === "colaborador";
    const openCard = status?.open;

    const getLocation = () => new Promise((resolve) => {
        if (!navigator.geolocation) return resolve(null);
        navigator.geolocation.getCurrentPosition(
            (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }),
            () => resolve(null),
            { enableHighAccuracy: true, timeout: 8000 }
        );
    });

    const punch = async (action) => {
        setBusy(true);
        try {
            const loc = await getLocation();
            await api.post(`/timecards/${action}`, loc || {});
            qc.invalidateQueries({ queryKey: ["timecards"] });
            qc.invalidateQueries({ queryKey: ["timecard-status"] });
            if (!loc) toast.info("Localização indisponível — ponto registrado sem GPS");
            toast.success(action === "clock-in" ? "Ponto de entrada registrado" : "Ponto de saída registrado");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div>
            <PageHeader
                overline="Cartão de ponto"
                title={isColaborador ? "Meu ponto" : "Cartões de ponto"}
                description={isColaborador ? "Registre sua entrada e saída com um toque." : "Acompanhe os registros de ponto de toda a equipe."}
            />

            {isColaborador && (
                <div className="mb-8 rounded-2xl border border-border bg-white p-8 text-center shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)]" data-testid="punch-card">
                    <div className={`mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full ${openCard ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>
                        <Clock className="h-7 w-7" />
                    </div>
                    {openCard ? (
                        <>
                            <p className="text-sm text-muted-foreground">Ponto aberto desde</p>
                            <p className="font-mono-nums text-3xl font-semibold text-primary">{formatTimeBR(openCard.clock_in)}</p>
                            <p className="mb-6 text-xs text-muted-foreground">{formatDateBR(openCard.clock_in)}</p>
                            <button onClick={() => punch("clock-out")} disabled={busy} data-testid="clock-out-btn" className="inline-flex items-center gap-2 rounded-full bg-expense px-8 py-3.5 font-medium text-white transition-transform hover:-translate-y-0.5 disabled:opacity-60">
                                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <LogOut className="h-5 w-5" />} Registrar saída
                            </button>
                        </>
                    ) : (
                        <>
                            <p className="mb-6 text-muted-foreground">Você não tem ponto em aberto.</p>
                            <button onClick={() => punch("clock-in")} disabled={busy} data-testid="clock-in-btn" className="inline-flex items-center gap-2 rounded-full bg-primary px-8 py-3.5 font-medium text-white transition-transform hover:-translate-y-0.5 disabled:opacity-60">
                                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <LogIn className="h-5 w-5" />} Registrar entrada
                            </button>
                        </>
                    )}
                </div>
            )}

            <Filters q={filters.q} start={filters.start} end={filters.end} onChange={setFilters} placeholder="Buscar por colaborador ou função..." />

            <div className="mt-6 overflow-hidden rounded-2xl border border-border bg-white" data-testid="timecards-table">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b border-border text-left text-xs uppercase tracking-widest text-muted-foreground">
                            {!isColaborador && <th className="px-5 py-4">Colaborador</th>}
                            <th className="px-5 py-4">Data</th>
                            <th className="px-5 py-4">Entrada</th>
                            <th className="px-5 py-4">Saída</th>
                            <th className="px-5 py-4 text-right">Duração</th>
                            <th className="px-5 py-4 text-right">Local</th>
                        </tr>
                    </thead>
                    <tbody>
                        {cards.length === 0 && (
                            <tr><td colSpan={isColaborador ? 5 : 6} className="px-5 py-10 text-center text-muted-foreground">Nenhum registro de ponto.</td></tr>
                        )}
                        {cards.map((c) => (
                            <tr key={c.id} className="border-b border-border/50 last:border-0 hover:bg-muted/40" data-testid={`timecard-row-${c.id}`}>
                                {!isColaborador && (
                                    <td className="px-5 py-4">
                                        <p className="font-medium">{c.user_name}</p>
                                        <span className="text-xs text-muted-foreground">{c.nivel}</span>
                                    </td>
                                )}
                                <td className="px-5 py-4 font-mono-nums text-muted-foreground">{formatDateBR(c.date)}</td>
                                <td className="px-5 py-4 font-mono-nums">{formatTimeBR(c.clock_in)}</td>
                                <td className="px-5 py-4 font-mono-nums">{c.clock_out ? formatTimeBR(c.clock_out) : <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">Em aberto</span>}</td>
                                <td className="px-5 py-4 text-right font-mono-nums font-medium">{durationBetween(c.clock_in, c.clock_out)}</td>
                                <td className="px-5 py-4 text-right">
                                    <div className="flex flex-col items-end gap-1">
                                        {c.clock_in_lat ? <a href={`https://www.google.com/maps?q=${c.clock_in_lat},${c.clock_in_lng}`} target="_blank" rel="noopener noreferrer" data-testid={`map-in-${c.id}`} className="inline-flex items-center gap-1 text-xs font-medium text-primary underline underline-offset-2 hover:text-accent"><MapPin className="h-3 w-3" /> Entrada</a> : null}
                                        {c.clock_out_lat ? <a href={`https://www.google.com/maps?q=${c.clock_out_lat},${c.clock_out_lng}`} target="_blank" rel="noopener noreferrer" data-testid={`map-out-${c.id}`} className="inline-flex items-center gap-1 text-xs font-medium text-primary underline underline-offset-2 hover:text-accent"><MapPin className="h-3 w-3" /> Saída</a> : null}
                                        {(!c.clock_in_lat && !c.clock_out_lat) && <span className="text-xs text-muted-foreground">—</span>}
                                        {(c.clock_in_address || c.clock_out_address) && (
                                            <span className="max-w-[220px] break-words text-[10px] leading-tight text-muted-foreground" title={c.clock_in_address || c.clock_out_address} data-testid={`address-${c.id}`}>
                                                {c.clock_in_address || c.clock_out_address}
                                            </span>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
